import { useState, useCallback, useEffect } from 'react'
import {
  Zap, Loader2, X,
} from 'lucide-react'
import { InlineWebhookPanel } from './components/InlineWebhookPanel'
import {
  inputCls, labelCls, SectionLabel, CopyButton, RawJsonCollapsible,
  EndpointPicker, PayloadEditor, CurlCollapsible,
} from './components/shared'
import type { EndpointDef } from './components/shared'
import { proxyUrl, buildHeaders } from './utils'
import type { Creds, LogEntry } from './types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj = Record<string, any>

// ── Constants ──────────────────────────────────────────────────────────────────

const V1_KYB_BASE = 'https://api.uat.frankie.one/compliance/v1.2'

const KYB_V1_ENDPOINTS: EndpointDef[] = [
  { id: 'ownership-query', method: 'POST', label: 'Query Ownership', path: '/business/ownership/query', category: 'Ownership', core: true },
  { id: 'get-entity', method: 'GET', label: 'Entity Details', path: '/entity/{entityId}', category: 'Entity Management', core: true },
  // Results
  { id: 'retrieve-response', method: 'GET', label: 'Retrieve Response', path: '/retrieve/response/{requestId}', description: '3-day cache', category: 'Results' },
  { id: 'get-checks', method: 'GET', label: 'Entity Checks', path: '/entity/{entityId}/checks', category: 'Results' },
  // Entity Management
  { id: 'delete-entity', method: 'DELETE', label: 'Delete Entity', path: '/entity/{entityId}', category: 'Entity Management' },
]

interface V1KybScenario { label: string; orgName: string; acn: string; tags: string[] }

const KYB_V1_SCENARIOS: V1KybScenario[] = [
  { label: 'FOX PTY. LTD. — Fast (~30s)', orgName: 'FOX PTY. LTD.', acn: '052121347', tags: ['Fast'] },
  { label: 'BCP (PITT STREET) PTY LTD — Medium (~1-2 min)', orgName: 'BCP (PITT STREET) PTY LTD', acn: '650193865', tags: ['Medium'] },
  { label: 'CLARO DISABILITY SERVICES PTY LTD — Complex (~5 min)', orgName: 'CLARO DISABILITY SERVICES PTY LTD', acn: '082554630', tags: ['Complex UBO'] },
  { label: 'BEACON LIGHTING GROUP LIMITED', orgName: 'BEACON LIGHTING GROUP LIMITED', acn: '164122785', tags: ['ASIC Profile', 'Complex UBO'] },
]

// ── V1 result panel ────────────────────────────────────────────────────────────

function V1KybResultPanel({ data, fullResponse }: { data: AnyObj; fullResponse: unknown }) {
  const entity = data?.entity
  const org = entity?.organisationData
  if (!entity && !data?.requestId) return null

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function kvpMap(notes: any[]): Record<string, string> {
    if (!Array.isArray(notes)) return {}
    const m: Record<string, string> = {}
    for (const n of notes) m[n.kvpKey] = n.kvpValue
    return m
  }
  const extra = kvpMap(entity?.extraData ?? [])
  const abn = extra['ABN'] || extra['abr.retrieved.abn']
  const acn = extra['ACN'] || extra['abr.retrieved.acn']
  const entityStatus = extra['abr.retrieved.entity_status']
  const entityStatusFrom = extra['abr.retrieved.entity_status.effective_from']
  const gstFrom = extra['abr.retrieved.gst_from_date']
  const gstTo = extra['abr.retrieved.gst_to_date']
  const jurisdiction = extra['controllingJurisdictionExpanded'] || extra['controllingJurisdiction']
  const statusOk = org?.status?.code === 'REGD'
  const addresses: AnyObj[] = entity?.addresses ?? []

  return (
    <div className="space-y-3">
      {org && (
        <div className="rounded-[11px] p-4" style={{ border: '1px solid #1e2c44', background: '#0e1828' }}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85] mb-3">Organisation</p>
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{
              background: statusOk ? 'rgba(52,211,153,.15)' : 'rgba(248,113,113,.15)',
              color: statusOk ? '#34d399' : '#f87171',
            }}>
              {String(org.status?.description ?? org.normalisedLegalStatus ?? '—')}
            </span>
            {org.type?.description && <span className="text-[10px] text-[#5b6b85]">{String(org.type.description)}</span>}
            {org.subclass?.description && <span className="text-[10px] text-[#5b6b85]">· {String(org.subclass.description)}</span>}
          </div>
          <div className="space-y-1.5">
            {[
              ['Registered Name', org.registeredName],
              ['ACN', acn], ['ABN', abn],
              ['Registration State', org.registration?.state],
              ['Registration Date', org.registration?.date],
              ['Jurisdiction', jurisdiction],
              ['Start Date', org.startDate],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={String(k)} className="flex gap-2 text-[10px]">
                <span className="text-[#5b6b85] font-mono w-36 shrink-0">{k}</span>
                <span className="text-[#94a3b8] font-mono">{String(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(entityStatus || gstFrom) && (
        <div className="rounded-[11px] p-4" style={{ border: '1px solid #1e2c44', background: '#0e1828' }}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85] mb-2">ABR Status</p>
          <div className="space-y-1.5">
            {[
              ['Entity Status', entityStatus ? `${entityStatus}${entityStatusFrom ? ` (from ${entityStatusFrom})` : ''}` : null],
              ['GST Registered', gstFrom ? `${gstFrom} → ${gstTo || 'current'}` : null],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={String(k)} className="flex gap-2 text-[10px]">
                <span className="text-[#5b6b85] font-mono w-36">{k}</span>
                <span className="text-[#94a3b8] font-mono">{String(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {addresses.length > 0 && (
        <div className="rounded-[11px] p-4" style={{ border: '1px solid #1e2c44', background: '#0e1828' }}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85] mb-2">Addresses ({addresses.length})</p>
          {addresses.slice(0, 4).map((a, i) => (
            <div key={i} className="flex items-start gap-2 text-[10px] mb-1.5">
              <span className="shrink-0 text-[9px] font-mono text-[#5b6b85] px-1 py-0.5 rounded"
                style={{ background: '#0a1322', border: '1px solid #233149' }}>{String(a.addressType ?? '—')}</span>
              <span className="text-[#94a3b8] font-mono">
                {[a.streetName, a.town, a.state, a.postalCode, a.country].filter(Boolean).join(', ') || `${a.state} ${a.postalCode}`}
              </span>
            </div>
          ))}
          {addresses.length > 4 && <p className="text-[9px] text-[#475569]">+{addresses.length - 4} more addresses</p>}
        </div>
      )}

      {data?.requestId && (
        <div className="text-[10px] font-mono text-[#5b6b85]">
          requestId: <span className="text-[#94a3b8]">{String(data.requestId)}</span>
        </div>
      )}

      <RawJsonCollapsible data={fullResponse} />
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

interface KybV1WorkspaceProps {
  creds: Creds
  addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void
}

export function KybV1Workspace({ creds, addLog }: KybV1WorkspaceProps) {
  const hasCreds = !!(creds.customerId && creds.apiKey)
  const v1KybCreds = { ...creds, apiBase: V1_KYB_BASE }

  const [selectedEndpoint, setSelectedEndpoint] = useState<string>('ownership-query')
  const [scenarioIdx, setScenarioIdx] = useState<number | null>(null)
  const [acnValue, setAcnValue] = useState('')
  const [idKeyType, setIdKeyType] = useState<'ACN' | 'ABN'>('ACN')
  const [checkTypes, setCheckTypes] = useState<string[]>(['one_plus', 'pep_media'])
  const [entityCategories, setEntityCategories] = useState<string[]>(['organisation', 'officers_directors', 'direct_owners', 'ubos'])
  const [resultLevel, setResultLevel] = useState('full')
  const [generateReport, setGenerateReport] = useState('UBO')
  const [ownershipMode, setOwnershipMode] = useState('full')
  const [entityId, setEntityId] = useState<string | null>(null)
  const [getEntityIdInput, setGetEntityIdInput] = useState('')
  const [requestId, setRequestId] = useState<string | null>(null)
  const [loading, setLoading] = useState<string | null>(null)
  const [result, setResult] = useState<AnyObj | null>(null)
  const [fullResult, setFullResult] = useState<unknown>(null)

  // PayloadEditor state
  const [editorText, setEditorText] = useState('')
  const [editorDirty, setEditorDirty] = useState(false)
  const [editorError, setEditorError] = useState<string | null>(null)

  const epDef = KYB_V1_ENDPOINTS.find(e => e.id === selectedEndpoint) ?? KYB_V1_ENDPOINTS[0]
  const isGetEndpoint = epDef.method === 'GET'

  const payload = {
    organisation: {
      entityType: 'ORGANISATION',
      extraData: [{ kvpKey: idKeyType, kvpValue: acnValue, kvpType: 'id.external' }],
    },
  }

  function buildQs() {
    const parts: string[] = []
    if (checkTypes.length) parts.push(`checkType=${checkTypes.join(',')}`)
    if (entityCategories.length) parts.push(`entityCategories=${entityCategories.join(',')}`)
    parts.push(`resultLevel=${resultLevel}`)
    if (generateReport) parts.push(`generateReport=${generateReport}`)
    parts.push(`ownershipMode=${ownershipMode}`)
    return parts.join('&')
  }

  const qs = buildQs()

  function dynamicPath(): string {
    const eid = entityId ?? getEntityIdInput
    if (selectedEndpoint === 'ownership-query') return `/business/ownership/query?${qs}`
    if (selectedEndpoint === 'get-entity') return `/entity/${eid || '{entityId}'}`
    return epDef.path
  }

  const fullUrl = `${V1_KYB_BASE}${dynamicPath()}`

  // Sync derived payload to editor
  const derivedJson = JSON.stringify(payload)
  useEffect(() => {
    if (!editorDirty && selectedEndpoint === 'ownership-query') {
      try {
        setEditorText(JSON.stringify(JSON.parse(derivedJson), null, 2))
      } catch {
        setEditorText(derivedJson)
      }
      setEditorError(null)
    }
  }, [derivedJson, editorDirty, selectedEndpoint])

  // Auto-populate GET input from prior results
  useEffect(() => {
    if (entityId && !getEntityIdInput) setGetEntityIdInput(entityId)
  }, [entityId, getEntityIdInput])

  function handleEndpointSelect(id: string) {
    setSelectedEndpoint(id)
    setEditorDirty(false)
    setEditorError(null)
  }

  function handleEditorChange(v: string) { setEditorText(v); setEditorDirty(true); setEditorError(null) }
  function resetEditor() { setEditorDirty(false); setEditorText(JSON.stringify(payload, null, 2)); setEditorError(null) }
  function getEditorPayload(): Record<string, unknown> | null {
    try { return JSON.parse(editorText) as Record<string, unknown> }
    catch { setEditorError('Invalid JSON — fix syntax before sending'); return null }
  }

  const doFetch = useCallback(async (method: string, path: string, body: unknown | undefined, annotation: string) => {
    const url = `${V1_KYB_BASE}${path}`
    const start = Date.now()
    const res = await fetch(proxyUrl(path), {
      method,
      headers: buildHeaders(v1KybCreds, method !== 'GET'),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
    const durationMs = Date.now() - start
    let data: unknown
    try { data = await res.json() } catch { data = {} }
    addLog({ method, url, status: res.status, durationMs, requestBody: body, responseBody: data, annotation })
    if (!res.ok) throw new Error((data as { error?: string })?.error ?? res.statusText)
    return data
  }, [v1KybCreds, addLog])

  async function submitQuery() {
    const finalPayload = getEditorPayload()
    if (!finalPayload) return
    setLoading('create'); setResult(null); setFullResult(null)
    try {
      const data = await doFetch('POST', `/business/ownership/query?${qs}`, finalPayload, 'V1: Business ownership query') as { entity?: { entityId?: string }; entityId?: string; requestId?: string }
      const eid = data?.entity?.entityId ?? data?.entityId
      const rid = data?.requestId
      if (eid) { setEntityId(eid); setGetEntityIdInput(eid) }
      if (rid) setRequestId(rid)
    } catch { /* logged */ } finally { setLoading(null) }
  }

  async function pollResult() {
    const eid = entityId ?? getEntityIdInput
    if (!eid) return
    setLoading('poll')
    try {
      const data = await doFetch('GET', `/entity/${eid}`, undefined, 'V1: Poll business ownership result') as AnyObj
      setResult(data); setFullResult(data)
    } catch { /* logged */ } finally { setLoading(null) }
  }

  function toggleCheckType(v: string) { setCheckTypes(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v]) }
  function toggleCategory(v: string) { setEntityCategories(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v]) }
  function reset() { setEntityId(null); setRequestId(null); setResult(null); setFullResult(null); setGetEntityIdInput('') }

  const curlBody = isGetEndpoint ? undefined : (editorDirty ? (getEditorPayload() ?? undefined) : payload)

  return (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: '200px minmax(350px, 400px) 1fr', alignItems: 'start', padding: '18px 0' }}
    >
      {/* ── COLUMN 1: Endpoint Picker ── */}
      <EndpointPicker
        endpoints={KYB_V1_ENDPOINTS}
        selectedId={selectedEndpoint}
        onSelect={handleEndpointSelect}
      />

      {/* ── COLUMN 2: Form Panel ── */}
      <div className="rounded-[14px] overflow-hidden" style={{ background: '#111c30', border: '1px solid #1e2c44' }}>
        {/* Header strip */}
        <div className="flex items-center gap-2.5 px-4 py-3" style={{ background: '#0e1828', borderBottom: '1px solid #1e2c44' }}>
          <span
            className="px-2 py-0.5 rounded text-[10px] font-bold font-mono"
            style={isGetEndpoint
              ? { background: 'rgba(96,165,250,.15)', color: '#60a5fa' }
              : { background: 'rgba(52,211,153,.13)', color: '#34d399' }}
          >
            {epDef.method}
          </span>
          <span className="text-[11px] font-mono text-[#8aa0c2] truncate flex-1">{epDef.path}</span>
          <span className="text-[9px] font-mono text-[#3f4f6b]">V1 API</span>
        </div>

        <div className="p-4 space-y-4">
          {/* ownership-query form */}
          {selectedEndpoint === 'ownership-query' && (
            <>
              <div>
                <SectionLabel>Test Scenarios</SectionLabel>
                <div className="flex flex-wrap gap-1.5">
                  {KYB_V1_SCENARIOS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => { setScenarioIdx(i); setAcnValue(s.acn) }}
                      className="flex items-center gap-1.5 px-2.5 py-[5px] rounded-[8px] text-[11px] font-medium transition-all"
                      style={scenarioIdx === i
                        ? { background: 'rgba(99,102,241,.16)', border: '1px solid rgba(99,102,241,.45)', color: '#e2e8f0' }
                        : { background: '#0f1a2e', border: '1px solid #233149', color: '#94a3b8' }
                      }
                    >
                      {s.orgName.split(' ')[0]}
                      {s.tags.map(t => (
                        <span key={t} className="text-[9px] px-1 py-0.5 rounded" style={{ background: 'rgba(20,184,166,.1)', color: '#2dd4bf' }}>{t}</span>
                      ))}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <SectionLabel>Organisation</SectionLabel>
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <label className={labelCls}>ID Type</label>
                    <select className={inputCls} value={idKeyType} onChange={e => setIdKeyType(e.target.value as 'ACN' | 'ABN')}>
                      <option value="ACN">ACN</option>
                      <option value="ABN">ABN</option>
                    </select>
                  </div>
                  <div className="col-span-3">
                    <label className={labelCls}>Number</label>
                    <input className={inputCls} value={acnValue} onChange={e => setAcnValue(e.target.value)} placeholder="052121347" />
                  </div>
                </div>
              </div>

              <div>
                <SectionLabel>Query Parameters</SectionLabel>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <p className="text-[10px] text-[#5b6b85] mb-1">checkType</p>
                    <div className="space-y-1">
                      {[['one_plus', 'one_plus (KYC 1+1)'], ['pep_media', 'pep_media (AML)']].map(([v, label]) => (
                        <label key={v} className="flex items-center gap-1.5 text-[10px] text-[#94a3b8] cursor-pointer">
                          <input type="checkbox" checked={checkTypes.includes(v)} onChange={() => toggleCheckType(v)} style={{ accentColor: '#6366f1' }} />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#5b6b85] mb-1">entityCategories</p>
                    <div className="space-y-1">
                      {['organisation', 'officers_directors', 'direct_owners', 'ubos'].map(v => (
                        <label key={v} className="flex items-center gap-1.5 text-[10px] text-[#94a3b8] cursor-pointer">
                          <input type="checkbox" checked={entityCategories.includes(v)} onChange={() => toggleCategory(v)} style={{ accentColor: '#6366f1' }} />
                          {v}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className={labelCls}>resultLevel</label>
                    <select className={inputCls} value={resultLevel} onChange={e => setResultLevel(e.target.value)}>
                      {['simple', 'summary', 'full'].map(v => <option key={v}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>generateReport</label>
                    <select className={inputCls} value={generateReport} onChange={e => setGenerateReport(e.target.value)}>
                      <option value="UBO">UBO</option>
                      <option value="SINGLE-LEVEL-AML">SINGLE-LEVEL-AML</option>
                      <option value="">none</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>ownershipMode</label>
                    <select className={inputCls} value={ownershipMode} onChange={e => setOwnershipMode(e.target.value)}>
                      <option value="full">full</option>
                      <option value="partial">partial</option>
                      <option value="onlyProfile">onlyProfile</option>
                    </select>
                  </div>
                </div>
                <p className="text-[9px] font-mono text-[#3f4f6b] mt-2 break-all">?{qs}</p>
              </div>

              <div className="space-y-2 pt-1">
                {!hasCreds && <p className="text-[10px] text-amber-400 text-center">Configure credentials above to make live calls</p>}
                <button
                  onClick={() => void submitQuery()}
                  disabled={loading !== null || !hasCreds || !acnValue}
                  className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
                  style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
                >
                  {loading === 'create'
                    ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Submitting…</span>
                    : 'Submit Query'
                  }
                </button>
              </div>
            </>
          )}

          {/* get-entity form */}
          {selectedEndpoint === 'get-entity' && (
            <>
              <div>
                <label className={labelCls}>Entity ID</label>
                <input
                  className={inputCls}
                  value={getEntityIdInput}
                  onChange={e => setGetEntityIdInput(e.target.value)}
                  placeholder="entityId from query step"
                />
              </div>

              <div className="space-y-2 pt-1">
                {!hasCreds && <p className="text-[10px] text-amber-400 text-center">Configure credentials above to make live calls</p>}
                <button
                  onClick={() => void pollResult()}
                  disabled={loading !== null || !hasCreds || !(entityId ?? getEntityIdInput)}
                  className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
                  style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
                >
                  {loading === 'poll'
                    ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Polling…</span>
                    : 'Poll Result'
                  }
                </button>
              </div>
            </>
          )}

          {/* entityId badge */}
          {entityId && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-[8px]" style={{ background: 'rgba(52,211,153,.06)', border: '1px solid rgba(52,211,153,.2)' }}>
              <span className="text-[10px] font-semibold text-emerald-400">entityId</span>
              <span className="text-[10px] font-mono text-[#94a3b8] flex-1 truncate">{entityId}</span>
              <CopyButton text={entityId} />
              <button onClick={reset} className="text-[#5b6b85] hover:text-red-400 transition-colors"><X size={10} /></button>
            </div>
          )}
          {requestId && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-[8px]" style={{ background: 'rgba(148,163,184,.04)', border: '1px solid #233149' }}>
              <span className="text-[10px] text-[#5b6b85]">requestId</span>
              <span className="text-[9px] font-mono text-[#94a3b8] flex-1 truncate">{requestId}</span>
            </div>
          )}

          <div className="text-[9px] font-mono text-[#3f4f6b]">
            <span className="text-[#5b6b85]">Base:</span> {V1_KYB_BASE}
          </div>
        </div>
      </div>

      {/* ── COLUMN 3: Results ── */}
      <div className="space-y-3 min-w-0">
        {/* PayloadEditor + cURL — above webhooks */}
        {!isGetEndpoint && (
          <PayloadEditor
            value={editorText}
            dirty={editorDirty}
            error={editorError}
            onChange={handleEditorChange}
            onReset={resetEditor}
          />
        )}
        <CurlCollapsible method={epDef.method} url={fullUrl} creds={v1KybCreds} body={curlBody} />

        <InlineWebhookPanel entityId={entityId} customerId={creds.customerId || null} />

        {!result && !loading && (
          <div
            className="flex flex-col items-center justify-center py-16 rounded-[14px]"
            style={{ border: '2px dashed #233149', background: '#0d1626' }}
          >
            <div className="w-12 h-12 rounded-[12px] flex items-center justify-center mb-4"
              style={{ background: 'rgba(99,102,241,.12)', border: '1px solid rgba(99,102,241,.2)' }}>
              <Zap size={22} className="text-[#6366f1]" />
            </div>
            <p className="text-[14px] font-semibold text-[#5b6b85]">KYB V1 Workspace</p>
            <p className="text-[11px] text-[#3f4f6b] mt-1.5 text-center max-w-xs">
              Select a test scenario or enter an ACN, configure query params, then submit
            </p>
            <p className="text-[10px] text-[#3f4f6b] mt-2 max-w-xs text-center">
              Step 1 returns an entityId. Step 2 polls until the report is ready (20–300s).
            </p>
          </div>
        )}

        {(loading === 'create' || loading === 'poll') && (
          <div className="p-5 rounded-[14px] space-y-3" style={{ background: '#0e1828', border: '1px solid #2a3754' }}>
            <div className="flex items-center gap-3">
              <Loader2 size={18} className="animate-spin text-amber-400 shrink-0" />
              <p className="text-[13px] font-semibold text-[#fcd34d]">
                {loading === 'create' ? 'Submitting query…' : 'Polling result…'}
              </p>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3" style={{ animation: 'fadeUp .25s ease' }}>
            <V1KybResultPanel data={result} fullResponse={fullResult} />
          </div>
        )}
      </div>
    </div>
  )
}
